import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { useToast } from '../../contexts/ToastContext';
import { Plus, Edit, Trash2, X, Save } from 'lucide-react';
import axios from 'axios';

// Component to display sponsor logo with signed URL
const SponsorLogoDisplay: React.FC<{ sponsor: Sponsor }> = ({ sponsor }) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (sponsor.logoUrl) {
        try {
          const response = await fetch(`/api/admin/sponsor-logo-url?key=${encodeURIComponent(sponsor.logoUrl)}`);
          const data = await response.json();
          if (data.success) {
            setLogoUrl(data.signedUrl);
          }
        } catch (error) {
          console.error('Failed to get signed URL for sponsor logo:', error);
        }
      }
      setLoading(false);
    };

    fetchSignedUrl();
  }, [sponsor.logoUrl]);

  if (loading) {
    return <div className="w-12 h-12 bg-gray-200 rounded animate-pulse"></div>;
  }

  if (!logoUrl) {
    return <div className="w-12 h-12 bg-gray-300 rounded flex items-center justify-center">
      <span className="text-gray-500 text-xs">No Logo</span>
    </div>;
  }

  return (
    <img 
      src={logoUrl} 
      alt={sponsor.name} 
      className="w-12 h-12 object-contain border rounded"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

interface Sponsor {
  name: string;
  logoUrl: string;
  website: string;
  order: number;
}

const SponsorManager: React.FC = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newSponsor, setNewSponsor] = useState<Partial<Sponsor>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const { success, error } = useToast();

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get('/api/admin/sponsors', { withCredentials: true });
      if (response.data.success) {
        setSponsors(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch sponsors:', err);
      if (err.response?.status === 401) {
        error('Authentication required. Please log in as an admin.');
      } else if (err.response?.status === 403) {
        error('Access denied. Admin privileges required.');
      } else {
        error('Failed to fetch sponsors');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile) return null;

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const response = await axios.post('/api/upload/sponsor-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      if (response.data.success) {
        return response.data.data.key || response.data.data.uploadDetails?.key || null;
      }
      return null;
    } catch (err) {
      console.error('Failed to upload logo:', err);
      error('Failed to upload logo');
      return null;
    }
  };

  const handleAddSponsor = async () => {
    if (!newSponsor.name || !logoFile) {
      error('Please provide sponsor name and logo');
      return;
    }

    try {
      const logoUrl = await uploadLogo();
      if (!logoUrl) return;

      const response = await axios.post('/api/admin/sponsors', {
        name: newSponsor.name,
        logoUrl,
        website: newSponsor.website || '',
        order: sponsors.length
      }, { withCredentials: true });

      if (response.data.success) {
        setSponsors([...sponsors, response.data.data]);
        setNewSponsor({});
        setLogoFile(null);
        setLogoPreview(null);
        success('Sponsor added successfully');
      }
    } catch (err) {
      console.error('Failed to add sponsor:', err);
      error('Failed to add sponsor');
    }
  };

  const showAddForm = () => {
    setNewSponsor({ name: '', website: '' });
  };

  const handleEditSponsor = async (index: number) => {
    const sponsor = sponsors[index];
    if (!sponsor.name || (!sponsor.logoUrl && !logoFile)) {
      error('Please provide sponsor name and logo');
      return;
    }

    try {
      let logoUrl = sponsor.logoUrl;
      if (logoFile) {
        const uploadedLogoUrl = await uploadLogo();
        if (!uploadedLogoUrl) return;
        logoUrl = uploadedLogoUrl;
      }

      const response = await axios.put(`/api/admin/sponsors/${index}`, {
        name: sponsor.name,
        logoUrl,
        website: sponsor.website || '',
        order: index
      }, { withCredentials: true });

      if (response.data.success) {
        const updatedSponsors = [...sponsors];
        updatedSponsors[index] = response.data.data;
        setSponsors(updatedSponsors);
        setEditingIndex(null);
        setLogoFile(null);
        setLogoPreview(null);
        success('Sponsor updated successfully');
      }
    } catch (err) {
      console.error('Failed to update sponsor:', err);
      error('Failed to update sponsor');
    }
  };

  const handleDeleteSponsor = async (index: number) => {
    if (!confirm('Are you sure you want to delete this sponsor?')) return;

    try {
      const response = await axios.delete(`/api/admin/sponsors/${index}`, { withCredentials: true });
      if (response.data.success) {
        const updatedSponsors = sponsors.filter((_, i) => i !== index);
        setSponsors(updatedSponsors);
        success('Sponsor deleted successfully');
      }
    } catch (err) {
      console.error('Failed to delete sponsor:', err);
      error('Failed to delete sponsor');
    }
  };

  const startEditing = async (index: number) => {
    setEditingIndex(index);
    setLogoFile(null);
    setLogoPreview(null);
    
    // Generate signed URL for existing logo if it exists
    const sponsor = sponsors[index];
    if (sponsor.logoUrl) {
      try {
        const response = await fetch(`/api/admin/sponsor-logo-url?key=${encodeURIComponent(sponsor.logoUrl)}`);
        const data = await response.json();
        if (data.success) {
          setLogoPreview(data.signedUrl);
        }
      } catch (error) {
        console.error('Failed to get signed URL for existing logo:', error);
      }
    }
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setLogoFile(null);
    setLogoPreview(null);
  };

  if (loading) {
    return <div className="text-center py-8">
      <div className="text-lg font-semibold mb-2">Loading sponsors...</div>
      <div className="text-sm text-gray-500">Fetching sponsor data from server</div>
      <div className="mt-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Manage Sponsors</h3>
        <Button onClick={showAddForm} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Sponsor
        </Button>
      </div>

      {/* Add New Sponsor Form */}
      {newSponsor.name !== undefined && (
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">Add New Sponsor</h4>
              <Button variant="ghost" size="sm" onClick={() => setNewSponsor({ name: undefined })}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Sponsor Name *</label>
                <Input
                  value={newSponsor.name || ''}
                  onChange={(e) => setNewSponsor({ ...newSponsor, name: e.target.value })}
                  placeholder="Enter sponsor name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Website (Optional)</label>
                <Input
                  value={newSponsor.website || ''}
                  onChange={(e) => setNewSponsor({ ...newSponsor, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Logo *</label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="cursor-pointer"
              />
              {logoPreview && (
                <div className="mt-2">
                  <img src={logoPreview} alt="Logo preview" className="w-20 h-20 object-contain border rounded" />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={handleAddSponsor} disabled={!newSponsor.name || !logoFile}>
                Add Sponsor
              </Button>
              <Button variant="outline" onClick={() => setNewSponsor({ name: undefined })}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Existing Sponsors */}
      <div className="space-y-4">
        {sponsors.map((sponsor, index) => (
          <Card key={index} className="p-4">
            {editingIndex === index ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Edit Sponsor</h4>
                  <Button variant="ghost" size="sm" onClick={cancelEditing}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Sponsor Name *</label>
                    <Input
                      value={sponsor.name}
                      onChange={(e) => {
                        const updatedSponsors = [...sponsors];
                        updatedSponsors[index].name = e.target.value;
                        setSponsors(updatedSponsors);
                      }}
                      placeholder="Enter sponsor name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Website (Optional)</label>
                    <Input
                      value={sponsor.website || ''}
                      onChange={(e) => {
                        const updatedSponsors = [...sponsors];
                        updatedSponsors[index].website = e.target.value;
                        setSponsors(updatedSponsors);
                      }}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Logo</label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="cursor-pointer"
                  />
                  {logoPreview && (
                    <div className="mt-2">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-20 h-20 object-contain border rounded" 
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => handleEditSponsor(index)}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={cancelEditing}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <SponsorLogoDisplay sponsor={sponsor} />
                  <div>
                    <h4 className="font-medium">{sponsor.name}</h4>
                    {sponsor.website && (
                      <a 
                        href={sponsor.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {sponsor.website}
                      </a>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEditing(index)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteSponsor(index)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {sponsors.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-700 mb-2">No Sponsors Yet</h4>
            <p className="text-gray-600">No sponsors have been added yet. Click "Add Sponsor" to get started.</p>
          </div>
          <Button onClick={showAddForm} className="flex items-center gap-2 mx-auto">
            <Plus className="w-4 h-4" />
            Add Your First Sponsor
          </Button>
        </div>
      )}
    </div>
  );
};

export default SponsorManager; 